// Each battery bank's chemistry, set per bank from the boat's Settings page
// (TOM-318, core ADR-197).
//
// The boat is authoritative, as for the vessel type (`vesselType.ts`). The page
// reads the boat's own snapshot (GET /v1/boats/:boatId/battery-chemistry), and
// a choice is RELAYED to the boat. The selected button is always the snapshot's
// `override`, never the click. Between the click and the boat's next snapshot
// the bank says "Sent to your boat…", and the page re-reads the snapshot until
// the boat confirms or the deadline passes.
//
// Changing a bank's chemistry does not change any alarm. The regenerate page
// offers the corrections, so the panel links there.

import {
  displayInstanceNumber,
  parseBatteryChemistrySnapshot,
  type BatteryChemistryBank,
  type BatteryChemistryChoice,
  type BatteryChemistrySnapshot,
} from '@neomaris/protocol';

import { api } from '../api';
import { isBoatOfflineRelayError } from './revealStaging';

export type { BatteryChemistryBank, BatteryChemistryChoice, BatteryChemistrySnapshot };

/** One bank's choice as the control offers it. `null` is automatic. */
export type BatteryChemistryOptionValue = BatteryChemistryChoice | null;

export interface BatteryChemistryOption {
  readonly value: BatteryChemistryOptionValue;
  readonly key: 'automatic' | BatteryChemistryChoice;
  readonly label: string;
}

/** The three choices, in offer order. */
export const BATTERY_CHEMISTRY_OPTIONS: readonly BatteryChemistryOption[] = [
  { value: null, key: 'automatic', label: 'Automatic' },
  { value: 'lithium', key: 'lithium', label: 'Lithium (LiFePO4)' },
  { value: 'lead_acid', key: 'lead_acid', label: 'Lead-acid (flooded, gel, AGM)' },
];

/** The word for a chemistry. */
export function batteryChemistryLabel(chemistry: BatteryChemistryChoice | null): string {
  if (chemistry === 'lithium') return 'lithium (LiFePO4)';
  if (chemistry === 'lead_acid') return 'lead-acid';
  return 'unknown';
}

/**
 * The name an owner recognises: the bank's reported name, else the name its
 * alarms carry ("Battery 2" for instance 1, "Battery 200" for instance 200,
 * "House battery" for `house`), with its system voltage. Numbered instances
 * are shown as every other surface shows them (`@neomaris/protocol`
 * `displayInstanceNumber`, TOM-631), so this row and the bank's alarm titles
 * say the same thing.
 */
export function batteryBankLabel(bank: BatteryChemistryBank): string {
  const instance = /^electrical\.batteries\.([^.]+)\.voltage$/.exec(bank.bank)?.[1] ?? bank.bank;
  const fallback = /^\d+$/.test(instance)
    ? `Battery ${displayInstanceNumber(instance)}`
    : `${instance.charAt(0).toUpperCase()}${instance.slice(1)} battery`;
  const name = bank.name ?? fallback;
  return bank.nominalVolts === null ? name : `${name} — ${bank.nominalVolts} V`;
}

/**
 * What the boat uses for this bank, and why. When the owner's choice differs
 * from what the battery itself reports, the second sentence says so, so an
 * owner who set the wrong chemistry sees the disagreement.
 */
export function batteryChemistryUsedText(bank: BatteryChemistryBank): string {
  if (bank.override !== null) {
    const used = `Your boat uses ${batteryChemistryLabel(bank.override)}, because you set it.`;
    if (!overridesDetection(bank) || bank.detected === null) return used;
    const reports =
      bank.detected.source === 'bms'
        ? `The battery reports a battery management system, which means ${batteryChemistryLabel(bank.detected.chemistry)}.`
        : `The battery reports ${batteryChemistryLabel(bank.detected.chemistry)}.`;
    return `${used} ${reports}`;
  }
  if (bank.detected === null) {
    return 'Your boat cannot tell this battery’s chemistry, so it uses lead-acid alarm values. Set it if this battery is lithium.';
  }
  const why =
    bank.detected.source === 'bms'
      ? 'it reports a battery management system'
      : 'the battery reports it';
  return `Your boat uses ${batteryChemistryLabel(bank.detected.chemistry)}, because ${why}.`;
}

/** Whether the owner's choice disagrees with what the battery reports. */
export function overridesDetection(bank: BatteryChemistryBank): boolean {
  return (
    bank.override !== null && bank.detected !== null && bank.detected.chemistry !== bank.override
  );
}

export interface BatteryChemistryMirror {
  readonly snapshot: BatteryChemistrySnapshot | null;
  readonly mirroredAt: string | null;
}

/**
 * Read a mirror response without trusting its shape. A snapshot that does not
 * parse reads as no snapshot, so the page shows "not reported yet" rather than
 * failing.
 */
export function toBatteryChemistryMirror(raw: unknown): BatteryChemistryMirror {
  const body = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const parsed =
    body.snapshot === null || body.snapshot === undefined
      ? null
      : parseBatteryChemistrySnapshot(body.snapshot);
  return {
    snapshot: parsed?.status === 'accepted' ? parsed.snapshot : null,
    mirroredAt: typeof body.mirroredAt === 'string' ? body.mirroredAt : null,
  };
}

export async function fetchBatteryChemistry(boatId: string): Promise<BatteryChemistryMirror> {
  return toBatteryChemistryMirror(await api<unknown>(`/v1/boats/${boatId}/battery-chemistry`));
}

/** What one bank's control is doing. Same shape as the vessel-type control. */
export type BatteryChemistrySaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending'; readonly value: BatteryChemistryOptionValue }
  | { readonly kind: 'sent'; readonly value: BatteryChemistryOptionValue }
  | { readonly kind: 'unconfirmed'; readonly value: BatteryChemistryOptionValue }
  | { readonly kind: 'offline' }
  | { readonly kind: 'error' };

const IDLE: BatteryChemistrySaveState = { kind: 'idle' };

/**
 * The boat's snapshot landed. End the in-flight request when the bank now
 * holds what was sent. Returns the same object when nothing changes.
 */
export function settleBatteryChemistry(
  save: BatteryChemistrySaveState,
  mirrored: BatteryChemistryOptionValue | undefined
): BatteryChemistrySaveState {
  if (save.kind !== 'sent' && save.kind !== 'unconfirmed') return save;
  return mirrored !== undefined && mirrored === save.value ? IDLE : save;
}

/** The echo never came. */
export function timeOutBatteryChemistry(
  save: BatteryChemistrySaveState
): BatteryChemistrySaveState {
  return save.kind === 'sent' ? { kind: 'unconfirmed', value: save.value } : save;
}

export function batteryChemistryStatusText(state: BatteryChemistrySaveState): string | null {
  switch (state.kind) {
    case 'sending':
      return 'Sending to your boat…';
    case 'sent':
      return 'Sent to your boat — this updates when your boat confirms it.';
    case 'unconfirmed':
      return 'Your boat has not confirmed that yet. It needs Neomaris Core 0.10.3 or newer. This updates when your boat picks it up.';
    case 'offline':
      return 'Your boat is offline. It keeps this setting, so bring it online and try again.';
    case 'error':
      return 'Couldn’t send that to your boat — try again.';
    case 'idle':
      return null;
  }
}

/** Relay one bank's choice. Never throws. */
export async function sendBatteryChemistry(
  boatId: string,
  bank: string,
  value: BatteryChemistryOptionValue
): Promise<BatteryChemistrySaveState> {
  try {
    await api(`/v1/boats/${boatId}/battery-chemistry`, {
      method: 'POST',
      body: { bank, chemistry: value },
      idempotencyKey: crypto.randomUUID(),
    });
    return { kind: 'sent', value };
  } catch (err) {
    return isBoatOfflineRelayError(err) ? { kind: 'offline' } : { kind: 'error' };
  }
}

/** Why the controls are disabled while the boat is unreachable. Visible text. */
export const BATTERY_CHEMISTRY_OFFLINE_REASON =
  'Your boat is offline — it keeps these settings, so they cannot be changed from here until it is back online.';
